'use client';

import './globals.css';
import { Inter } from 'next/font/google';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

const inter = Inter({ subsets: ['latin'] });
type User = {
  email?: string;
  tenantId?: string;
  tenantName?: string;
  role?: string;
  termsAcceptedAt?: string | null;
  avatarUrl?: string | null;
};

function NavIcon({ type }: { type: 'home' | 'compose' | 'settings' | 'ops' | 'info' | 'posts' }) {
  if (type === 'home') return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>;
  if (type === 'compose') return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg>;
  if (type === 'settings') return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h-.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>;
  if (type === 'info') return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>;
  if (type === 'posts') return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h16M4 12h16M4 18h16"></path></svg>;
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>;
}

function useIdleTimeout(onTimeout: () => void, timeoutMs: number = 60 * 60 * 1000) {
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const resetTimer = () => { clearTimeout(timeoutId); timeoutId = setTimeout(onTimeout, timeoutMs); };
    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart'];
    resetTimer();
    events.forEach(event => window.addEventListener(event, resetTimer));
    return () => { clearTimeout(timeoutId); events.forEach(event => window.removeEventListener(event, resetTimer)); };
  }, [onTimeout, timeoutMs]);
}

export default function RootLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname() || '';
  const [isInitializing, setIsInitializing] = useState(true);
  const [mounted, setMounted] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);

  const isLoginPath = pathname === '/' || pathname.startsWith('/login');
  const isOpsPath = pathname.startsWith('/ops');
  const isTermsPath = pathname === '/terms-agreement';

  useEffect(() => {
    setMounted(true);
    
    // --- 再起動 & タイムアウトの強力なガード ---
    if (typeof window !== 'undefined' && !isLoginPath && !isOpsPath) {
      
      // 1. タイムアウトフラグが残っている場合 (アドレスバー直接書き換え等) は強制排除
      if (sessionStorage.getItem('ax_timed_out') === 'true') {
        sessionStorage.removeItem('ax_timed_out');
        window.location.href = '/login';
        return;
      }

      // 2. ブラウザ再起動検知
      const isSessionActive = sessionStorage.getItem('ax_session_active');
      if (!isSessionActive) {
        console.log("Browser session restarted. Clearing persistent data.");
        localStorage.removeItem('user');
        fetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
        sessionStorage.setItem('ax_session_active', 'true');
        window.location.href = '/login';
        return;
      }
    }
    
    sessionStorage.setItem('ax_session_active', 'true');

    let isRedirecting = false;

    const initialize = async () => {
      try {
        const stored = localStorage.getItem('user');
        let currentUser: User | null = null;

        if (stored) {
          try {
            currentUser = JSON.parse(stored) as User;
            setUser(currentUser);
          } catch (e) {
            console.error("Failed to parse user from storage", e);
          }
        }

        if (isLoginPath || isOpsPath) {
          setIsInitializing(false);
          return;
        }

        if (!currentUser) {
          isRedirecting = true;
          window.location.href = '/login';
          return;
        }

        try {
          const statusRes = await fetch('/api/me/status', { credentials: 'include' });
          if (statusRes.ok) {
            const statusData = await statusRes.json();
            currentUser.termsAcceptedAt = statusData.termsAcceptedAt;
            setUser(currentUser);
            localStorage.setItem('user', JSON.stringify(currentUser));
          } else if (statusRes.status === 401 || statusRes.status === 403) {
            console.log("Session expired or invalid. Clearing local storage.");
            localStorage.removeItem('user');
            window.location.href = '/login';
            return;
          }
        } catch (e) {
          console.error("Failed to fetch terms status", e);
        }

        if (!currentUser.termsAcceptedAt && !isTermsPath) {
          isRedirecting = true;
          window.location.href = '/terms-agreement';
          return;
        }
        if (currentUser.termsAcceptedAt && isTermsPath) {
          isRedirecting = true;
          window.location.href = '/home';
          return;
        }

        try {
          const res = await fetch('/api/settings', { credentials: 'include' });
          if (res.ok) {
            const data = await res.json();
            const updatedUser = { ...currentUser, tenantName: data.tenantName ?? currentUser.tenantName };
            setUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));
          }
        } catch (e) {
          console.error("Failed to fetch latest user info", e);
        }

      } catch (err) {
        console.error("Initialization error:", err);
      } finally {
        if (!isRedirecting) {
          setIsInitializing(false);
        }
      }
    };

    initialize();
  }, [pathname, isLoginPath, isOpsPath, isTermsPath]);

  // ★ ログアウト処理の完全同期化
  const handleLogout = async () => {
    localStorage.clear();
    sessionStorage.removeItem('ax_timed_out');
    // クッキー削除が完了するまで画面遷移を待つ
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
    } catch(e) {}
    window.location.href = '/login';
  };

  // ★ タイムアウト時の完全遮断処理
  useIdleTimeout(() => {
    if (!isLoginPath && user && user.tenantId !== 'admin-tenant') {
      // 1. サーバーへクッキーの削除を依頼 (httpOnlyクッキーはJSで消せないためAPI必須)
      fetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
      
      // 2. ローカルのユーザー情報を「即座に」破壊
      localStorage.removeItem('user');
      
      // 3. タイムアウトしたというフラグを立てる (アドレスバーからのリロード対策)
      sessionStorage.setItem('ax_timed_out', 'true');
      
      setShowTimeoutModal(true);
    }
  });

  const navItems = [
    { href: '/home', label: 'ホーム', icon: 'home' as const },
    { href: '/compose', label: '投稿作成', icon: 'compose' as const },
    { href: '/posts', label: '投稿管理', icon: 'posts' as const },
    { href: '/announcements', label: 'お知らせ', icon: 'info' as const },
    { href: '/settings', label: '設定', icon: 'settings' as const },
    ...(user?.tenantId === 'admin-tenant' ? [{ href: '/ops', label: '管理者画面', icon: 'ops' as const }] : []),
  ];

  const content = isLoginPath || isOpsPath || isTermsPath ? (
    children
  ) : (
    <div className="ax-shell">
      <aside className="ax-sidebar">
        <div>
          <div className="ax-brand-title" style={{ lineHeight: 0, marginBottom: '1.5rem', marginTop: '0.2rem' }}>
            <img src="/logo.png" alt="AutoXpress" style={{ height: '36px', width: 'auto', maxWidth: '100%', objectFit: 'contain', display: 'block' }} />
          </div>
          <div className="ax-brand-subtitle">ソーシャルメディア管理</div>
        </div>

        <nav className="ax-nav">
          {navItems.map((item) => {
            const active = pathname === item.href || pathname.startsWith(`${item.href}/`);
            return (
              <Link key={item.href} href={item.href} className={`ax-nav-link${active ? ' active' : ''}`}>
                <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><NavIcon type={item.icon} /></span>
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="ax-sidebar-footer">
          <button className="ax-btn" style={{ width: '100%' }} onClick={() => (window.location.href = '/compose')}>
            投稿する
          </button>
          <div className="ax-user-chip">
            {user?.avatarUrl ? (
              <img src={user.avatarUrl} alt="Avatar" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
            ) : (
              <div className="ax-avatar" style={{ flexShrink: 0 }}>{(user?.email || 'A').slice(0, 1).toUpperCase()}</div>
            )}
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, wordBreak: "break-all" }}>{user?.tenantName || 'AutoXpress User'}</div>
              <div className="ax-muted" style={{ fontSize: 11, wordBreak: "break-all" }}>{user?.email || 'guest@example.com'}</div>
            </div>
          </div>
        </div>
      </aside>

      <div className="ax-shell-main">
        <header className="ax-topbar">
          <div className="ax-top-actions">
            <button className="ax-btn-danger" onClick={handleLogout}>ログアウト</button>
          </div>
        </header>
        {children}
      </div>
    </div>
  );

  return (
    <html lang="ja" suppressHydrationWarning>
      <body className={inter.className} suppressHydrationWarning>
        {(!mounted || isInitializing) ? (
          <main style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#f7fafc', color: '#a0aec0', fontSize: '1.2rem', margin: 0 }}>
            Loading...
          </main>
        ) : (
          <>
            {content}
            {showTimeoutModal && (
              <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                <div style={{ backgroundColor: '#fff', padding: '32px', borderRadius: '12px', width: '400px', maxWidth: '90%', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                  <h3 style={{ margin: '0 0 16px 0', fontSize: '1.25rem', color: '#e53e3e', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
                    セッションタイムアウト
                  </h3>
                  <p style={{ margin: '0 0 24px 0', color: '#4a5568', lineHeight: 1.5 }}>
                    一定時間操作がなかったため自動的にログアウトしました。<br/>再度ログインしてください。
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => { setShowTimeoutModal(false); handleLogout(); }}
                      style={{ backgroundColor: '#3182ce', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', transition: 'background-color 0.2s' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2b6cb0'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3182ce'}
                    >
                      ログイン画面へ
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </body>
    </html>
  );
}
